import { PropsWithChildren } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { Icon, IconName } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { PopoverDialog, PopoverMenuItem, Text, usePopoverDialog, useTooltip } from '@frontend/design-system';
import { FilterCount } from './filter-count';

interface Props {
  text: string;
  iconName: IconName;
  disabled?: boolean;
  filterCount?: number;
}

export const PopoverMenuButton = ({ text, iconName, disabled, filterCount, children }: PropsWithChildren<Props>) => {
  const { t } = useTranslation('schedule');
  const { triggerProps, Tooltip, tooltipProps } = useTooltip({ placement: 'top' });
  const { getTriggerProps, getDialogProps } = usePopoverDialog();

  return (
    <>
      <PopoverMenuItem css={menuItemStyle(disabled)} {...(disabled ? triggerProps : getTriggerProps())}>
        <Icon name={iconName} />
        <Text css={{ flex: 1, textAlign: 'left' }} color={disabled ? 'disabled' : 'default'}>
          {text}
        </Text>
        {!disabled && <FilterCount count={filterCount} />}
        <Icon name='alt-caret-right-tiny' />
      </PopoverMenuItem>
      {disabled && (
        <Tooltip {...tooltipProps}>
          {text} {t('coming soon')}
        </Tooltip>
      )}
      <PopoverDialog {...getDialogProps()} returnFocus={false}>
        {children}
      </PopoverDialog>
    </>
  );
};

const menuItemStyle = (disabled?: boolean) =>
  css({
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 1),
    margin: theme.spacing(0.5, 0),
    transition: 'background-color 0.2s ease',
    borderRadius: theme.borderRadius.small,
    ':hover': {
      backgroundColor: disabled ? 'initial' : theme.colors.neutral5,
    },
    cursor: disabled ? 'not-allowed' : 'pointer',
    '& > svg': {
      color: disabled ? theme.font.colors.disabled : theme.colors.neutral50,
    },
  });
